// **React Imports
import {
  Fragment,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router-dom'

// **Context Imports
import { SettingsContext } from '../../../../../context/setting'

// **API Imports
import {
  attemptLogs,
  attemptsVideos,
  getAuxiliaryEvidence,
  getContestAttemptStatus,
  getMCQEvaluations,
  newAttemptsVideos
} from '../../../../../axios/api/contest'
import { getAllTopicsForQuestion } from '../../../../../axios/api/question'

// **Utils Imports
import DeveloperTagModal from '../../../../../components/developer-tag-modal'
import {
  CHALLENGE_TYPES,
  PSYCHOMETRIC_COLORS,
  PSYCHOMETRIC_STATUS,
  PSYCHOMETRIC_TRAIT_LABELS,
  TIMELINE_EVENT_KEY
} from '../../../../../utils/constant'
import { TOOLTIPS } from '../../../../../utils/tooltips'
import {
  checkDeveloperAccess,
  checkPublicRoute,
  convertToISO,
  hexToRGB,
  mainContainerOverflow,
  showErrorToast,
  showRemarkPopup,
  swalAlert
} from '../../../../../utils/utils'

// **Redux Imports
import { useSelector } from 'react-redux'

// **MUI Imports
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  Divider,
  Grid,
  LinearProgress,
  Slider,
  Tooltip,
  Typography
} from '@mui/material'

// **Component Imports
import RenderScore from './component/RenderScore'
import RadarChart from '../../../../../components/charts/radar'
import Chip from '../../../../../components/chip'
import CustomDataGrid from '../../../../../components/data-grid'
import DraggableWindow from '../../../../../components/draggable-window'
import Icon from '../../../../../components/icon'
import McqsPreviewer from '../../../../../components/mcqs-previewer'
import OverlayLoading from '../../../../../components/overlay-loading'
import RejectQualifyCandidate from '../../../../../components/reject-qualify-candidate'
import ScoreBadge from '../../../../../components/score-badge'
import SEOComponent from '../../../../../seo'
import PlayRecording from './PlayRecording'
import CodeTimelineFilter from './component/CodeTimelineFilter'

// **Logo Imports
import LogoDark from '../../../../../assets/images/logo-dark.svg'
import LogoLight from '../../../../../assets/images/logo-light.svg'

// **Third Party Imports
import debounce from 'lodash.debounce'
import Swal from 'sweetalert2'

// **SCSS Imports
import AddRemark from '../../../../../components/add-remark'
import './EvaluateMCQChallenge.scss'
import AddTag from './component/AddTag'
import AuxiliaryDeviceEvidence from './component/AuxiliaryDeviceEvidence'
import AuxiliaryDeviceEvidenceDrawer from './component/AuxiliaryDeviceEvidenceDrawer'
import CustomTooltip from './component/CustomTooltip'
import DependencyChangeIcon from './component/DependencyChangeIcon'
import EvaluateGazeInfoModal from './component/EvaluateGazeInfoModal'
import GoBackButton from './component/GoBackButton'
import RemarkTimelineModal from './component/RemarkTimelineModal'
import {
  COPYCODESVG,
  DEVELOPERTAGSVG,
  EXITSVG,
  IPSWITCHEDSVG,
  NOFACESDETECTEDSVG,
  STAGEFINISHEDSVG,
  TABSWITCHSVG,
  VIEWREMARKSVG
} from './component/TimelineIcons'
import ViewProfile from './component/ViewProfile'
import {
  getTimeDifferenceInSeconds,
  modalStyle,
  streamContainerStyle
} from './component/timelineUtils'
import './index.scss'

const MAX_CHARACTER_CHANGED = 20

// QuestionChanged SVG - specific to MCQ
const QuestionChanged = memo(({ questionNo }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 56 56"
      fill="none"
    >
      <g id="number-badge">
        <circle
          id="circle-outline"
          cx="28"
          cy="28"
          r="27"
          stroke="currentColor"
          strokeWidth="2"
        />
        <foreignObject
          width="100%"
          height="100%"
          x="0"
          y="0"
          className="progress-text-container"
        >
          <div
            className="progress-text"
            xmlns="http://www.w3.org/1999/xhtml"
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '100%',
              fontSize: questionNo > 99 ? '24px' : '36px',
              fontWeight: 'bold',
              margin: 0,
              color: 'currentColor',
              textAlign: 'center'
            }}
          >
            {questionNo}
          </div>
        </foreignObject>
      </g>
    </svg>
  )
})

// Custom Mark component to handle dynamic styles
const CustomMark = memo(({ mark, max, onDeveloperTagClick }) => {
  // variables
  const height = useMemo(() => '20px', [])

  const questionNoChanged = useMemo(() => {
    return (mark?.event?.eventDetails?.questionIndex ?? -1) + 1
  }, [mark])

  return (
    <>
      <Box
        className="line-marker"
        style={{
          height: height,
          left: `${(mark.value / (max || 1)) * 100}%`,
          backgroundColor: 'var(--primary-main)'
        }}
      />
      <Icon
        title={
          mark?.event?.event === 'question-changed' ? (
            `Changed to question number ${questionNoChanged}`
          ) : mark?.event?.event === 'ipAddressChange' ? (
            TOOLTIPS.ipAddressChange
          ) : mark?.event?.event === 'noFaceDetected' ? (
            mark?.event?.eventDetails?.imageUrl ? (
              <CustomTooltip title={TOOLTIPS.noFaceDetected} />
            ) : (
              TOOLTIPS.noFaceDetected
            )
          ) : mark?.event?.event === 'tabSwitch' ? (
            mark?.event?.eventDetails?.imageUrl ? (
              <CustomTooltip title={TOOLTIPS.tabSwitch} />
            ) : (
              TOOLTIPS.tabSwitch
            )
          ) : mark?.event?.event === 'dependencyChange' ? (
            TOOLTIPS.dependencyChange
          ) : mark?.event?.event === 'candidateRemark' ? (
            mark?.event?.eventDetails?.remark ? (
              <CustomTooltip title={TOOLTIPS.viewRemarks} forRemark={true} />
            ) : (
              TOOLTIPS.viewRemarks
            )
          ) : mark?.event?.event === 'developerTag' ? (
            <CustomTooltip
              title={TOOLTIPS.viewDeveloperTag}
              developerTag={true}
            />
          ) : mark?.event?.event === 'manualLeave' ? (
            TOOLTIPS.manualLeave
          ) : mark?.event?.event === 'stageFinished' ? (
            TOOLTIPS.stageFinished
          ) : (
            TOOLTIPS.codeCopyPasted
          )
        }
        className="icon-marker"
        isSvgElement={true}
        SvgComponent={
          mark?.event?.event === 'question-changed' ? (
            <QuestionChanged questionNo={questionNoChanged} />
          ) : mark?.event?.event === 'ipAddressChange' ? (
            <IPSWITCHEDSVG />
          ) : mark?.event?.event === 'noFaceDetected' ? (
            <NOFACESDETECTEDSVG />
          ) : mark?.event?.event === 'tabSwitch' ? (
            <TABSWITCHSVG />
          ) : mark?.event?.event === 'dependencyChange' ? (
            <DependencyChangeIcon />
          ) : mark?.event?.event === 'candidateRemark' ? (
            <VIEWREMARKSVG />
          ) : mark?.event?.event === 'developerTag' ? (
            <DEVELOPERTAGSVG />
          ) : mark?.event?.event === 'manualLeave' ? (
            <EXITSVG />
          ) : mark?.event?.event === 'stageFinished' ? (
            <STAGEFINISHEDSVG />
          ) : (
            <COPYCODESVG />
          )
        }
        sx={{
          display: 'flex',
          color: 'var(--primary-main)',
          left: `calc(${(mark.value / (max || 1)) * 100}% - 11px)`,
          cursor:
            mark?.event?.eventDetails?.imageUrl ||
            mark?.event?.eventDetails?.remark ||
            mark?.event?.event === 'developerTag'
              ? 'pointer'
              : 'auto'
        }}
        onClick={() => {
          if (mark?.event?.event === 'developerTag') {
            onDeveloperTagClick?.(mark?.event)
            return
          }
          if (mark?.event?.eventDetails?.remark) {
            showRemarkPopup(mark)
          }
          if (mark?.event?.eventDetails?.imageUrl) {
            Swal.fire({
              imageAlt: 'Evidence',
              padding: 10,
              title:
                mark?.event?.event === 'noFaceDetected'
                  ? TOOLTIPS.noFaceDetected
                  : TOOLTIPS.tabSwitch,
              imageUrl: mark?.event?.eventDetails?.imageUrl
            })
          }
        }}
      />
    </>
  )
})

const MarksBreakdownItem = memo(
  ({
    isActive = false,
    icon,
    label,
    value,
    handleBreakdownChange,
    dynamicFont = false
  }) => {
    return (
      <Tooltip>
        <Box
          className={`sidepanel-breakdown-item ${isActive ? 'active' : ''}`}
          onClick={handleBreakdownChange}
        >
          <Box
            className={`sidepanel-breakdown-item-index-container ${
              isActive ? 'active' : ''
            }`}
            sx={{
              ...(icon
                ? {
                    border: 'none !important',
                    backgroundColor: 'transparent !important'
                  }
                : {}),

              ...(dynamicFont
                ? {
                    fontSize:
                      typeof value === 'number'
                        ? value > 99
                          ? '13px !important'
                          : '18px !important'
                        : ''
                  }
                : {})
            }}
          >
            {icon ? <Icon icon={icon} /> : value}
          </Box>

          <Typography fontSize={'9px'} textAlign={'center'}>
            {label}
          </Typography>
        </Box>
      </Tooltip>
    )
  }
)

export const PsychometricTraitCard = memo(
  ({ forPdf = false, trait, dominantStyle, secondaryStyle }) => (
    <Box
      sx={{
        border: '1px solid var(--divider)',
        borderRadius: '12px',
        padding: '20px',
        height: forPdf ? 'auto' : '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        pageBreakInside: 'avoid'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 700
                // textTransform: 'capitalize'
              }}
            >
              {trait?.name}
            </Typography>
            {dominantStyle?.toLowerCase() === trait?.name?.toLowerCase() && (
              <Chip
                label="Primary"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '11px',
                  fontWeight: 600,
                  color: trait?.color,
                  bgcolor: `${trait?.color}15`
                }}
              />
            )}
            {secondaryStyle?.toLowerCase() === trait?.name?.toLowerCase() && (
              <Chip
                label="Secondary"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '11px',
                  fontWeight: 600,
                  color: trait?.color,
                  bgcolor: `${trait?.color}15`
                }}
              />
            )}
          </Box>
          <Typography
            sx={{
              fontSize: '13px',
              color: trait?.color,
              fontWeight: 600
            }}
          >
            {trait?.subTitle}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: '28px',
            fontWeight: 800,
            color: trait?.color,
            lineHeight: 1
          }}
        >
          {trait?.score}
          <Typography
            component="span"
            sx={{
              fontSize: '12px',
              color: 'var(--text-secondary)'
            }}
          >
            {' '}
            /100
          </Typography>
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={trait?.score}
        sx={{
          height: '8px',
          borderRadius: '4px',
          backgroundColor: 'var(--action-hover)',
          '& .MuiLinearProgress-bar': {
            backgroundColor: trait?.color,
            borderRadius: '4px'
          }
        }}
      />

      <Typography
        sx={{
          fontSize: '13px',
          lineHeight: 1.6,
          color: 'var(--text-secondary)',
          mt: 'auto'
        }}
      >
        {trait?.description}
      </Typography>
    </Box>
  )
)

export const PsychometricResultUnavailable = memo(
  ({ attemptedQuestions, totalQuestions }) => (
    <Box
      sx={{
        p: 2,
        backgroundColor: 'var(--background-paper)',
        borderRadius: '8px',
        border: '1px solid var(--divider)',
        borderLeft: '4px solid var(--warning-main)',
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}
    >
      <Icon
        icon="mdi:alert-outline"
        style={{
          fontSize: '24px',
          color: 'var(--warning-main)',
          flexShrink: 0
        }}
      />
      <Box>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: '14px',
            color: 'var(--text-primary)',
            mb: 0.25
          }}
        >
          Result Unavailable
        </Typography>
        <Typography sx={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
          The candidate attempted <b>{attemptedQuestions}</b> out of{' '}
          <b>{totalQuestions}</b> questions, which is not sufficient to generate
          the result.
        </Typography>
      </Box>
    </Box>
  )
)

export const PsychometricTestLayout = memo(
  ({ testData, forPdf, isSideBySide = false }) => {
    const traitEntries = useMemo(() => {
      return Object.entries(testData?.traits || {}).map(
        ([key, score], index) => {
          const level = testData?.traitLevels?.[key]
          return {
            key,
            name: PSYCHOMETRIC_TRAIT_LABELS[key] || key,
            score: score ?? 0,
            subTitle: level?.title || '',
            description: level?.description || '',
            color: PSYCHOMETRIC_COLORS[index]
          }
        }
      )
    }, [testData?.traits, testData?.traitLevels])

    const radarIndicators = useMemo(
      () => traitEntries?.map((t) => t?.name),
      [traitEntries]
    )
    const radarValues = useMemo(
      () => traitEntries?.map((t) => t?.score),
      [traitEntries]
    )

    const dominantStyle = useMemo(
      () =>
        testData?.primaryDisc
          ? PSYCHOMETRIC_TRAIT_LABELS[testData?.primaryDisc] ||
            testData?.primaryDisc
          : null,
      [testData?.primaryDisc]
    )

    const secondaryStyle = useMemo(
      () =>
        testData?.secondaryDisc
          ? PSYCHOMETRIC_TRAIT_LABELS[testData?.secondaryDisc] ||
            testData?.secondaryDisc
          : null,
      [testData?.secondaryDisc]
    )

    const archetypeName = useMemo(
      () => testData?.archeType?.archetype || '',
      [testData?.archeType?.archetype]
    )
    const archetypeDescription = useMemo(
      () => testData?.archeType?.summary || '',
      [testData?.archeType?.summary]
    )
    const strength = useMemo(
      () => testData?.archeType?.strength || '',
      [testData?.archeType?.strength]
    )
    const suggestedRoles = useMemo(
      () => testData?.archeType?.suggestedRoles || [],
      [testData?.archeType?.suggestedRoles]
    )

    return (
      <>
        <Grid container sx={{ mb: 4, alignItems: 'center' }}>
          <Grid item xs={12} md={isSideBySide ? 6 : 12}>
            {/* ArcheType + Description */}
            {archetypeName && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: hexToRGB('#3498db'),
                    mb: 0.5,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '22px'
                  }}
                >
                  {archetypeName}
                </Typography>
                <Typography
                  sx={{
                    color: 'var(--text-secondary)',
                    lineHeight: 1.6,
                    fontSize: '16px'
                  }}
                >
                  {archetypeDescription}
                </Typography>
              </Box>
            )}
          </Grid>

          <Grid item xs={12} md={isSideBySide ? 6 : 12}>
            {/* Radar Chart - Centered */}
            <Box
              sx={{
                width: '100%',
                maxWidth: 550,
                mx: 'auto',
                pageBreakInside: 'avoid'
              }}
            >
              <Box sx={{ height: isSideBySide ? 270 : 320 }}>
                <RadarChart
                  indicators={radarIndicators}
                  values={radarValues}
                  max={100}
                  showFullLengthTitle
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {traitEntries?.map((trait) => (
            <Grid item xs={12} md={6} key={trait?.key}>
              <PsychometricTraitCard
                trait={trait}
                forPdf={forPdf}
                dominantStyle={dominantStyle}
                secondaryStyle={secondaryStyle}
              />
            </Grid>
          ))}
        </Grid>

        {/* Footer Info (Strengths & Roles) */}
        {(strength || suggestedRoles?.length > 0) && (
          <Box
            sx={{
              border: '1px solid var(--divider)',
              borderRadius: '12px',
              p: '20px',
              pageBreakInside: 'avoid'
            }}
          >
            <Grid container spacing={4}>
              {strength && (
                <Grid item xs={12} md={suggestedRoles?.length > 0 ? 6 : 12}>
                  <Typography
                    sx={{
                      fontSize: '15px',
                      fontWeight: 700,
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <Icon
                      icon="mdi:star-outline"
                      color="var(--primary-main)"
                      fontSize={20}
                    />
                    Key Strengths
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5
                    }}
                  >
                    {strength
                      ?.split('. ')
                      ?.filter((s) => s?.trim())
                      .map((sentence, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1.5
                          }}
                        >
                          <Icon
                            icon="mdi:check-circle-outline"
                            color="var(--success-main)"
                            fontSize={18}
                            style={{ marginTop: '2px', flexShrink: 0 }}
                          />
                          <Typography
                            sx={{
                              fontSize: '13.5px',
                              color: 'var(--text-secondary)'
                            }}
                          >
                            {sentence?.endsWith('.')
                              ? sentence
                              : `${sentence}.`}
                          </Typography>
                        </Box>
                      ))}
                  </Box>
                </Grid>
              )}
              {suggestedRoles?.length > 0 && (
                <Grid item xs={12} md={strength ? 6 : 12}>
                  <Typography
                    sx={{
                      fontSize: '15px',
                      fontWeight: 700,
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <Icon
                      icon="mdi:briefcase-outline"
                      color="var(--primary-main)"
                      fontSize={20}
                    />
                    Suggested Roles
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {suggestedRoles?.map((role, idx) => (
                      <Chip
                        key={idx}
                        label={role}
                        sx={{
                          backgroundColor: 'var(--action-hover)',
                          border: '1px solid var(--divider)',
                          borderRadius: '8px',
                          fontWeight: 500,
                          fontSize: '13px',
                          px: 1
                        }}
                      />
                    ))}
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </>
    )
  }
)

const PsychometricReport = ({ testData }) => {
  const title = useMemo(
    () => testData?.title || `Personality Analysis`,
    [testData]
  )
  if (!testData) return null

  return (
    <Accordion
      defaultExpanded
      sx={{
        borderRadius: '12px !important',
        overflow: 'hidden',
        border: '1px solid var(--divider)',
        boxShadow: 'none !important',
        '&:before': { display: 'none' },
        margin: '0px'
      }}
    >
      <AccordionSummary
        expandIcon={<Icon icon="mdi:chevron-down" />}
        sx={{
          minHeight: '64px',
          py: 2,
          '& .MuiAccordionSummary-content': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            pr: 2
          }
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: 'var(--text-primary)' }}
        >
          {title}
        </Typography>
        {testData?.totalQuestions != null && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: 'var(--grey-100)',
              px: 1.5,
              py: 0.5,
              borderRadius: '6px'
            }}
          >
            <Typography
              fontSize="12px"
              color="var(--grey-600)"
              fontWeight={600}
            >
              Attempted:{' '}
              <Typography
                component="span"
                fontSize="12px"
                fontWeight={800}
                color="var(--grey-800)"
              >
                {testData.attemptedQuestions || 0}/{testData.totalQuestions}
              </Typography>{' '}
              Questions
            </Typography>
          </Box>
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ p: 4, pt: 2 }}>
        {testData?.status === PSYCHOMETRIC_STATUS.INSUFFICIENT_DATA ? (
          <PsychometricResultUnavailable
            attemptedQuestions={testData?.attemptedQuestions}
            totalQuestions={testData?.totalQuestions}
          />
        ) : (
          <PsychometricTestLayout testData={testData} title={title} />
        )}
      </AccordionDetails>
    </Accordion>
  )
}

const MCQSummarySection = memo(
  ({ data, eventData, preferredTopics, navigate }) => {
    const topicWiseBreakdownColumns = useMemo(
      () => [
        {
          field: 'topicName',
          headerName: 'Topic',
          display: 'flex',
          minWidth: 150,
          flex: 0.2
        },
        {
          field: 'totalQuestions',
          headerName: '#Questions',
          display: 'flex',
          minWidth: 150,
          flex: 0.2,
          valueGetter: (value) => value || 0
        },
        {
          field: 'attempted',
          headerName: 'Attempted',
          display: 'flex',
          minWidth: 100,
          flex: 0.2,
          valueGetter: (value) => value || 0
        },
        {
          field: 'unattempted',
          headerName: 'Unattempted',
          display: 'flex',
          minWidth: 100,
          flex: 0.2,
          valueGetter: (value) => value || 0
        },
        {
          field: 'score',
          headerName: 'Score',
          display: 'flex',
          minWidth: 150,
          flex: 0.2,
          renderCell: ({ row }) => (
            <ScoreBadge acquired={row?.score} total={row?.totalScore} />
          )
        }
      ],
      []
    )

    const handleViewTimeline = useCallback(() => {
      navigate(`${window.location.pathname}?filter=timeline`)
    }, [navigate])

    return (
      <Box className="evaluate-report-main-container">
        <Box className="candidate-evaluate-report-container">
          <Card
            className="card-main-wrapper"
            sx={{ display: 'flex', overflowY: 'auto' }}
          >
            <Box className="header-section">
              <Box className="general-details">
                <Typography className="heading">{data?.name}</Typography>
                <Typography className="sub-heading">{data?.email}</Typography>
              </Box>
              <Box>
                <Typography className="heading">
                  {data?.challengeName || ''}
                </Typography>

                {data?.stageType !== CHALLENGE_TYPES.PSYCHOMETRIC && (
                  <Typography className="sub-heading">
                    <RenderSkills skills={preferredTopics} />
                  </Typography>
                )}
              </Box>
            </Box>
            <Divider sx={{ margin: '0px' }} />
            {data?.stageType === CHALLENGE_TYPES.MCQ && (
              <>
                {' '}
                <Grid
                  container
                  columnSpacing={4}
                  rowGap={4}
                  className="candidate-score-details"
                >
                  <Grid item xs={12} lg={9}>
                    <Box
                      className="container-card event-details"
                      sx={{ height: '100%' }}
                    >
                      <Typography className="section-heading" mb={2}>
                        Topic-wise Quiz Breakdown
                      </Typography>
                      <Box
                        sx={{
                          borderRadius: '6px',
                          border: '1px solid var(--divider)'
                        }}
                      >
                        <CustomDataGrid
                          needPagination={false}
                          columns={topicWiseBreakdownColumns}
                          rows={data?.topicWiseMarkBreakDown || []}
                          rowHeight={50}
                          disableColumnSorting={true}
                          getRowId={(row) => {
                            return row?.topicId
                          }}
                        />
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} lg={3}>
                    <Box className="score-container">
                      <Box className="container-card">
                        <Typography className="section-heading">
                          Overall Score
                        </Typography>
                        <Typography
                          color={'var(--primary-main)'}
                          fontSize={'52px'}
                          fontWeight={900}
                        >
                          {data?.score}
                          <span
                            style={{
                              fontSize: '18px',
                              color: 'var(--text-secondary)'
                            }}
                          >
                            /{data?.challengeTotalScore}
                          </span>
                        </Typography>

                        <Chip
                          label={`Percentage Score: ${data?.percentageScore || 0}%`}
                          color={'primary'}
                          variant={'contained'}
                        />
                      </Box>

                      <Box className="container-card">
                        <Typography className="section-heading">
                          View Recording
                        </Typography>
                        <Box
                          className="video-container"
                          onClick={handleViewTimeline}
                        >
                          <Icon icon={'gridicons:play'} />
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </>
            )}
            {data?.stageType === CHALLENGE_TYPES.PSYCHOMETRIC && (
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
              >
                {Object.values(data?.psychometricReport || {}).map(
                  (item, index) => (
                    <PsychometricReport key={`${index}`} testData={item} />
                  )
                )}
              </Box>
            )}
          </Card>
        </Box>
      </Box>
    )
  }
)

const CodeTimeline = ({
  marks,
  paused,
  position,
  setPaused,
  eventFilter,
  setPosition,
  handleEventFilterChange,
  handleSetTimelineEventData,
  attemptId,
  handleRefreshAttemptEvent,
  isPublicRoute,
  gazeSegments
}) => {
  // State
  const [speed, setSpeed] = useState(1)

  const [hoverPosition, setHoverPosition] = useState(null)
  const [openAddRemarkModal, setOpenAddRemarkModal] = useState(false)
  const [modalTimeStamp, setModalTimeStamp] = useState(null)
  const [developerTagModalData, setDeveloperTagModalData] = useState({
    open: false,
    data: null
  })
  const sliderRef = useRef(null)

  // variables
  const duration = useMemo(() => {
    if (marks?.length > 1) {
      const startTime = new Date(marks?.startTime)
      const endTime = new Date(marks?.[marks.length - 1]?.endTime)
      return parseInt((endTime - startTime) / 1000) + 2
    } else {
      return 0
    }
  }, [marks])

  const isDeveloper = useMemo(() => checkDeveloperAccess(), [])

  const allowRemark = useMemo(
    () => !isPublicRoute && !isDeveloper,
    [isPublicRoute, isDeveloper]
  )

  // Handlers
  const handleSpeedChange = useCallback((increase = true) => {
    setSpeed((prev) => {
      if (increase && prev < 4) {
        return prev + 1
      } else if (!increase && prev > 1) {
        return prev - 1
      } else {
        return prev
      }
    })
  }, [])
  const handlePause = useCallback(
    (value) => {
      setPaused(value)
    },
    [setPaused]
  )

  function formatDuration(value) {
    const minute = Math.floor(value / 60)
    const secondLeft = value - minute * 60
    return `${minute}:${secondLeft < 10 ? `0${secondLeft}` : secondLeft}`
  }

  const handlePassedMark = useCallback(
    (currentPosition) => {
      const passedEventMark = marks?.filter(
        (mark) => mark.value <= currentPosition && mark?.event
      )

      if (passedEventMark?.length > 0) {
        handleSetTimelineEventData(
          passedEventMark?.[passedEventMark?.length - 1] || null,
          passedEventMark
        )
      }
    },
    [marks, handleSetTimelineEventData]
  )

  const handleSliderHover = useCallback(
    (event) => {
      const sliderRect = sliderRef.current.getBoundingClientRect()
      const x = event.clientX - sliderRect.left
      const sliderWidth = sliderRect.width

      const timeValue = Math.round((x / sliderWidth) * duration)
      const timeStamp = new Date(marks?.startTime)
      timeStamp.setSeconds(timeStamp.getSeconds() + timeValue)

      setHoverPosition({ x, timeStamp })
    },
    [duration, marks?.startTime]
  )
  const handleOpenRemarkModal = useCallback((timestamp) => {
    setOpenAddRemarkModal(true)
    setModalTimeStamp(timestamp)
  }, [])

  const handleCloseRemarkModal = useCallback(() => {
    setOpenAddRemarkModal(false)
    setModalTimeStamp(null)
  }, [])

  const handleOpenDeveloperTagModal = useCallback((data) => {
    setDeveloperTagModalData({ open: true, data })
  }, [])

  const handleCloseDeveloperTagModal = useCallback(() => {
    setDeveloperTagModalData({ open: false, data: null })
  }, [])

  //useEffect
  useEffect(() => {
    let interval
    if (!paused) {
      interval = setInterval(() => {
        setPosition((prev) => {
          handlePassedMark(prev)
          const newPosition = prev + speed
          if (newPosition <= duration) {
            return newPosition
          } else {
            setPaused(true)
            clearInterval(interval)
            return duration
          }
        })
      }, 1000)
    }
    return () => clearInterval(interval)
  }, [speed, paused, setPaused, duration, setPosition, handlePassedMark])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        padding: '5px 20px 10px',
        gap: '4px',
        borderBottom: '1px solid rgba(var(--customColors-mainRgb), 0.2)'
      }}
    >
      <Box className="evaluate-attempt-timeline-container">
        <Box
          className="slider-marker-container"
          {...(allowRemark && {
            onMouseLeave: () => setHoverPosition(null)
          })}
        >
          {(eventFilter?.['gazeSegments'] ||
            eventFilter?.['violationSegments']) &&
            gazeSegments?.map((segment, index) => (
              <Box
                key={index}
                sx={{
                  position: 'absolute',
                  height: '4px',
                  zIndex: 2,
                  pointerEvents: 'none',
                  background: 'var(--warning-dark)',
                  left: `${(segment.start_ms / 1000 / (duration || 1)) * 100}%`,
                  width: `${(segment.duration_ms / 1000 / (duration || 1)) * 100}%`
                }}
              />
            ))}

          <Box className="line-marker-container">
            <Box className="line-marker-inner-container">
              {marks?.map((mark, index) =>
                eventFilter?.[mark?.event?.event] ? (
                  <CustomMark
                    key={index}
                    mark={mark}
                    maxCharacterChange={MAX_CHARACTER_CHANGED}
                    max={duration}
                    onDeveloperTagClick={handleOpenDeveloperTagModal}
                  />
                ) : (
                  <Fragment key={index}></Fragment>
                )
              )}
            </Box>
          </Box>

          <Slider
            ref={sliderRef}
            key={`${duration}`}
            aria-label="time-indicator"
            size="small"
            value={position}
            min={0}
            step={1}
            marks={marks}
            max={duration}
            onChange={(_, value) => {
              setPosition(value)
              handlePassedMark(value)
            }}
            sx={{
              '& .MuiSlider-mark': {
                display: 'none'
              },
              '& .MuiSlider-markActive': {
                background: '#000'
              }
            }}
            {...(allowRemark && { onMouseMove: handleSliderHover })}
          />
          {hoverPosition && duration > 0 && allowRemark && (
            <>
              <Box
                sx={{
                  height: '20px',
                  backgroundColor: 'var(--primary-main)',
                  transform: 'transformX(-50%)',
                  width: '2px',
                  position: 'absolute',
                  top: '-6px',
                  left: hoverPosition?.x
                }}
              />
              <Tooltip title="Add Remark" placement="top" arrow>
                <Box
                  sx={{
                    position: 'absolute',
                    top: '-20px',
                    left: hoverPosition?.x - 10,
                    cursor: 'pointer',
                    background: 'var(--primary-main)',
                    padding: '4px 4px',
                    borderRadius: '4px',
                    color: 'var(--common-white)',
                    fontSize: '12px',
                    zIndex: 10
                  }}
                  onClick={() =>
                    handleOpenRemarkModal(hoverPosition?.timeStamp)
                  }
                >
                  <Icon
                    icon={'streamline:chat-bubble-square-write-remix'}
                    style={{ fontSize: '12px' }}
                  />
                </Box>
              </Tooltip>
            </>
          )}
        </Box>

        <Box className="timeline-label">
          <Typography className="time">{`${formatDuration(
            position
          )} / ${formatDuration(duration)}`}</Typography>
          <Box className="timeline-controls">
            <Icon
              cursor="pointer"
              icon={'ic:sharp-fast-rewind'}
              onClick={() => handleSpeedChange(false)}
            />
            <Icon
              cursor="pointer"
              icon={!paused ? 'ph:pause-fill' : 'ph:play-fill'}
              onClick={() => handlePause(!paused)}
            />
            <Icon
              cursor="pointer"
              icon={'ic:sharp-fast-forward'}
              onClick={() => handleSpeedChange(true)}
            />
          </Box>
          <Typography className="time-speed">{`${speed}x`}</Typography>
        </Box>
      </Box>
      <CodeTimelineFilter
        eventList={eventFilter}
        handleFilterChange={handleEventFilterChange}
      />
      {allowRemark && (
        <RemarkTimelineModal
          open={openAddRemarkModal}
          handleClose={handleCloseRemarkModal}
          positionTime={modalTimeStamp}
          attemptId={attemptId}
          handleRefreshAttemptEvent={handleRefreshAttemptEvent}
        />
      )}
      <DeveloperTagModal
        open={developerTagModalData.open}
        data={developerTagModalData.data}
        onClose={handleCloseDeveloperTagModal}
        handleRefreshData={handleRefreshAttemptEvent}
      />
    </Box>
  )
}

const RenderSkills = ({ skills }) => {
  const maxLength = 3
  return skills?.length > 0 ? (
    <Typography component={'span'}>
      <b>Selected Skills</b>: {skills?.slice(0, maxLength).join(', ')}
      {skills.length > maxLength && <span>{', '}</span>}
      {skills.length > maxLength && (
        <Tooltip title={skills?.slice(maxLength).join(', ')}>
          <Typography
            component={'span'}
            sx={{ textDecoration: 'underline', color: 'primary.main' }}
          >
            +{skills?.length - maxLength} More
          </Typography>
        </Tooltip>
      )}
    </Typography>
  ) : null
}

const EvaluateMCQAttempt = ({ assessment }) => {
  // Hooks
  const navigate = useNavigate()
  const { state } = useLocation()
  const { attemptId, contestId, publicId = null } = useParams()
  const [searchParams, setSearchParam] = useSearchParams()

  // Context
  const {
    setSettings,
    settings: { mode }
  } = useContext(SettingsContext)

  // Redux
  const selectedWorkspace = useSelector(
    (state) => state.workspace.selectedWorkspace
  )

  const currentlyActivePlan = useSelector(
    (state) => state.auth?.currentlyActivePlan
  )

  // Refs
  const webcamRef = useRef({
    ref: null,
    duration: 0,
    currentTime: null,
    previousUrl: null,
    fileName: null
  })
  const screenShareRef = useRef({
    ref: null,
    duration: 0,
    currentTime: null,
    previousUrl: null,
    fileName: null
  })

  // states
  const [data, setData] = useState(null)
  const [topics, setTopics] = useState([])
  const [paused, setPaused] = useState(true)
  const [position, setPosition] = useState(0)
  const [eventData, setEventData] = useState([])
  const [filterQuestions, setFilterQuestions] = useState([])
  const [auxiliaryEvidence, setAuxiliaryEvidence] = useState([])
  const [eventFilter, setEventFilter] = useState({})
  const [attemptStatusDetails, setAttemptStatusDetails] = useState(null)
  const [selectedFilter, setSelectedFilter] = useState(
    searchParams.get('filter') || 'summary'
  )
  const [updateQuestionIndex, setUpdateQuestionIndex] = useState(null)
  const [attemptRecordings, setAttemptRecordings] = useState([])
  const [newAttemptRecordings, setNewAttemptRecordings] = useState([])
  const [loading, setLoading] = useState({
    data: false,
    topic: false,
    eventLogs: false,
    recordings: false,
    status: false
  })
  const [gazeSegments, setGazeSegments] = useState([])

  const [minimiseInfo, setMinimiseInfo] = useState({
    webcam: false,
    screen: false,
    auxiliary: false
  })
  const [expandInfo, setExpandInfo] = useState({
    webcam: false,
    screen: false,
    auxiliary: false
  })
  const [showOnTop, setShowOnTop] = useState({
    webcam: false,
    screen: false,
    auxiliary: false
  })

  // Variable
  const isNewSync = useMemo(() => {
    const syncTest = searchParams.get('syncTest') || null
    return syncTest != null
      ? syncTest === 'new'
        ? true
        : false
      : newAttemptRecordings?.length > 0
        ? true
        : false
  }, [searchParams, newAttemptRecordings])

  const hasAccess = useMemo(() => {
    return !!currentlyActivePlan
  }, [currentlyActivePlan])

  const isPublicRoute = useMemo(() => {
    return checkPublicRoute()
  }, [])

  const isDeveloper = useMemo(() => {
    return checkDeveloperAccess()
  }, [])

  const TIMELINE_EVENT_KEY_NEW = useMemo(() => {
    return isDeveloper
      ? [...TIMELINE_EVENT_KEY, 'developerTag']
      : TIMELINE_EVENT_KEY
  }, [isDeveloper])

  const candidateDetails = useMemo(() => {
    return data
      ? {
          email: data?.email || '',
          name: data?.name || '',
          candidateId: data?.candidateId || 0
        }
      : null
  }, [data])

  const challengeDetails = useMemo(() => {
    return data
      ? {
          name: data?.challengeName || ''
        }
      : null
  }, [data])

  const preferredTopics = useMemo(() => {
    return topics?.length > 0 && data?.preferredTopicIds
      ? data?.preferredTopicIds
          ?.map((topicId) => topics.find((topic) => topic.id === topicId)?.name)
          .filter(Boolean)
      : []
  }, [topics, data?.preferredTopicIds])

  const sortedMarks = useMemo(() => {
    if (eventData?.length > 0) {
      const markers = []
      const newData = eventData?.sort((a, b) =>
        new Date(a?.timestamp) > new Date(b?.timestamp) ? 1 : -1
      )

      const startTime = newData?.[0]?.timestamp
        ? new Date(newData?.[0]?.timestamp)
        : new Date()

      newData?.forEach((item) => {
        const endTime = new Date(item?.timestamp)

        markers.push({
          index: markers?.length + 1,
          data: null,
          event: item,
          value: parseInt((endTime - startTime) / 1000),
          changedFile: null,
          endTime
        })
      })

      markers.startTime = newData?.[0]?.timestamp
      return markers
    }
    return []
  }, [eventData])

  // Handlers
  const handleNavigate = useCallback(() => {
    navigate(
      isPublicRoute && publicId
        ? `/share/${publicId}`
        : state?.pathname
          ? state?.pathname
          : `/${
              assessment ? 'assessments' : 'contests'
            }/${contestId}/edit?active=dashboard`,
      {
        replace: true
      }
    )
  }, [navigate, state, assessment, contestId, isPublicRoute, publicId])

  const handleSelectFilter = useCallback(
    (value) => {
      searchParams.set('filter', value)
      setSearchParam(searchParams, { state })
    },
    [searchParams, setSearchParam, state]
  )

  const fetchAuxiliaryEvidence = useCallback(async (attemptId) => {
    const response = await getAuxiliaryEvidence(attemptId)
    if (response?.isError) {
      showErrorToast(response)
    } else {
      setAuxiliaryEvidence(response)
    }
  }, [])

  const debouncedEventHandler = useMemo(
    () =>
      debounce((data, eventData) => {
        if (!eventData?.length || !data) return

        const lastQuestionChange = eventData
          ?.filter(
            (event) =>
              event?.event?.event === 'question-changed' &&
              event?.index <= data?.index
          )
          ?.slice(-1)?.[0]

        setUpdateQuestionIndex(
          lastQuestionChange?.event?.eventDetails?.questionIndex || 0
        )
      }, 500),
    []
  )

  const handleSetTimelineEventData = useCallback(
    (data, eventData) => {
      debouncedEventHandler(data, eventData)
    },
    [debouncedEventHandler]
  )

  const handleViewGazeTimeline = useCallback((segments) => {
    setGazeSegments(segments)
  }, [])

  const fetchAttemptsRecording = useCallback(async (attemptId) => {
    setLoading((prev) => ({ ...prev, recordings: true }))
    const response = await attemptsVideos(attemptId)
    const newVideosResponse = await newAttemptsVideos(attemptId)
    setLoading((prev) => ({ ...prev, recordings: false }))
    if (response?.isError) {
      setAttemptRecordings([])
    } else {
      setAttemptRecordings(response)
    }

    if (newVideosResponse?.isError) {
      setNewAttemptRecordings([])
    } else {
      setNewAttemptRecordings(newVideosResponse)
    }
  }, [])

  const fetchEvaluationData = useCallback(async (attemptId) => {
    setLoading((prev) => ({ ...prev, data: true }))
    const response = await getMCQEvaluations(attemptId)
    setLoading((prev) => ({ ...prev, data: false }))

    if (!response?.questions?.length > 0) {
      setSelectedFilter('all')
    }

    if (response?.isError) {
      showErrorToast(response)
      setData(null)
    } else {
      if (response?.attemptStatus === 'IN_PROGRESS') {
        swalAlert({
          text: 'The assessment is still ongoing, so the results generated at this stage are partial',
          icon: 'info',
          confirmButtonText: 'OK',
          showCancelButton: false
        })
      }

      setData(response)
    }
  }, [])

  const fetchTopics = useCallback(async (workspaceId) => {
    setLoading((prev) => ({ ...prev, topic: true }))
    const response = await getAllTopicsForQuestion(workspaceId)
    setLoading((prev) => ({ ...prev, topic: false }))

    if (response?.isError) {
      showErrorToast(response)
      setTopics([])
    } else {
      setTopics(response)
    }
  }, [])

  const fetchAttemptsEvent = useCallback(
    async (attemptId) => {
      setLoading((prev) => ({ ...prev, eventLogs: true }))
      const response = await attemptLogs(attemptId)
      setLoading((prev) => ({ ...prev, eventLogs: false }))

      if (response?.isError) {
        showErrorToast(response)
        setEventData([])
        setEventFilter({})
      } else {
        const obj = {}
        response?.forEach((event) => {
          if (
            obj?.[event?.event] === undefined &&
            TIMELINE_EVENT_KEY_NEW.includes(event?.event)
          ) {
            obj[event?.event] = true
          }
        })
        setEventData(response)
        setEventFilter(obj)
      }
    },
    [TIMELINE_EVENT_KEY_NEW]
  )

  const handleRefreshAttemptEvent = useCallback(async () => {
    if (attemptId) {
      await fetchAttemptsEvent(attemptId)
    }
  }, [attemptId, fetchAttemptsEvent])

  const fetchAttemptStatusDetails = useCallback(async (attemptId) => {
    setLoading((prev) => ({ ...prev, status: true }))
    const response = await getContestAttemptStatus(attemptId)
    setLoading((prev) => ({ ...prev, status: false }))

    if (response?.isError) {
      showErrorToast(response)
    } else {
      setAttemptStatusDetails(response)
    }
  }, [])

  const handleRefreshAttemptStatus = useCallback(() => {
    if (attemptId) {
      fetchAttemptStatusDetails(attemptId)
    }
  }, [attemptId, fetchAttemptStatusDetails])

  const handleVideoSync = useCallback(
    (ref, fileName, marks, position, paused, isNewSync, attemptRecordings) => {
      if (marks?.length > 0 && attemptRecordings?.length > 0) {
        const localDate = new Date(marks?.startTime)
        localDate.setSeconds(localDate.getSeconds() + position)

        const filterVideos = []
        attemptRecordings?.forEach((item) => {
          const splitName = item?.fileName?.split(isNewSync ? '_' : '__')

          if (splitName?.length === 2) {
            let date
            let name

            if (isNewSync) {
              name = splitName?.[0]
              date = new Date(Number(splitName?.[1]?.split('.')?.[0]))
            } else {
              name = splitName?.[1]
              const dateObj = splitName?.[0]
              date = new Date(convertToISO(dateObj))
            }

            if (name?.startsWith(fileName) && date < localDate) {
              filterVideos.push({ ...item, date })
            }
          }
        })

        const filterVideo = filterVideos?.slice(-1)?.[0]

        if (filterVideo) {
          const timeDiff = getTimeDifferenceInSeconds(
            filterVideo?.date,
            localDate?.toISOString()
          )
          if (
            ref.current.duration > 0 &&
            ref.current.currentTime !== null &&
            timeDiff >= ref.current.duration
          ) {
            if (ref.current.previousUrl !== '') {
              ref.current.previousUrl = ''
              ref.current.fileName = null
              ref.current.ref.src = ''
              ref.current.ref.load()
            }
          } else {
            if (ref.current.previousUrl !== filterVideo?.url) {
              ref.current.previousUrl = filterVideo?.url
              ref.current.fileName = filterVideo?.fileName
              ref.current.ref.src = filterVideo?.url
            }

            if (ref.current.currentTime !== null) {
              const videoTimeDiff = Math.abs(timeDiff - ref.current.currentTime)

              if (videoTimeDiff > 1 || videoTimeDiff < -1) {
                ref.current.ref.currentTime = timeDiff
              }
            } else if (ref.current.ref && ref.current.ref.readyState >= 1) {
              ref.current.ref.currentTime = timeDiff
            }

            if (paused) {
              if (!ref.current.ref.paused) {
                ref.current.ref.pause()
              }
            } else {
              if (ref.current.ref.paused) {
                ref.current.ref.play().catch((error) => {
                  if (error.name !== 'AbortError') {
                    console.error('Error during video playback:', error)
                  }
                })
              }
            }
          }
        } else {
          if (ref.current.previousUrl !== '') {
            ref.current.previousUrl = ''
            ref.current.fileName = null
            ref.current.ref.src = ''
            ref.current.ref.load()
          }
        }
      } else {
        if (ref.current.previousUrl !== '') {
          ref.current.previousUrl = ''
          ref.current.fileName = null
          ref.current.ref.src = ''
          ref.current.ref.load()
        }
      }
    },
    []
  )

  const handleEventFilterChange = useCallback((eventName, value) => {
    setEventFilter((prev) => ({ ...prev, [eventName]: value }))
  }, [])

  //useEffect
  useEffect(() => {
    mainContainerOverflow()
    setSettings((prev) => ({
      ...prev,
      appBarHidden: true,
      removePadding: true,
      contentWidth: 'full',
      hideSubscriptionStrap: true,
      sidePanel: { ...prev.sidePanel, hidden: true },
      secondaryNavBarConfig: { ...prev.secondaryNavBarConfig, show: false }
    }))

    return () => {
      mainContainerOverflow(false)
      setSettings((prev) => ({
        ...prev,
        appBarHidden: false,
        removePadding: false,
        contentWidth: 'boxed',
        hideSubscriptionStrap: false,
        sidePanel: { ...prev.sidePanel, hidden: false },
        secondaryNavBarConfig: { ...prev.secondaryNavBarConfig, show: true }
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (gazeSegments?.length > 0) {
      setEventFilter((prev) => {
        const newFilters = { ...prev }
        delete newFilters.gazeSegments
        delete newFilters.violationSegments
        newFilters[
          gazeSegments?.[0]?.isViolationType
            ? 'violationSegments'
            : 'gazeSegments'
        ] = true
        return newFilters
      })
    } else {
      setEventFilter((prev) => {
        const newObj = { ...prev }
        delete newObj.gazeSegments
        delete newObj.violationSegments
        return newObj
      })
    }
  }, [gazeSegments])

  useEffect(() => {
    setSelectedFilter(searchParams.get('filter') || 'summary')
  }, [searchParams])

  useEffect(() => {
    if (!assessment && attemptId && !isPublicRoute) {
      fetchAttemptStatusDetails(attemptId)
    }
  }, [attemptId, assessment, fetchAttemptStatusDetails, isPublicRoute])

  useEffect(() => {
    if (attemptId) {
      fetchEvaluationData(attemptId)
      fetchAttemptsEvent(attemptId)
      fetchAttemptsRecording(attemptId)
      fetchAuxiliaryEvidence(attemptId)
    }
  }, [
    attemptId,
    fetchEvaluationData,
    fetchAttemptsEvent,
    fetchAttemptsRecording,
    fetchAuxiliaryEvidence
  ])

  useEffect(() => {
    setPosition(0)
    setPaused(true)

    webcamRef.current = {
      ref: null,
      duration: 0,
      currentTime: null,
      previousUrl: null,
      fileName: null
    }

    screenShareRef.current = {
      ref: null,
      duration: 0,
      currentTime: null,
      previousUrl: null,
      fileName: null
    }
  }, [selectedFilter])

  useEffect(() => {
    if (data?.questions?.length > 0) {
      setFilterQuestions(data?.questions)
    } else {
      setFilterQuestions([])
    }
  }, [selectedFilter, data?.questions])

  useEffect(() => {
    if (selectedWorkspace?.id && !isPublicRoute) {
      fetchTopics(selectedWorkspace?.id)
    }
  }, [selectedWorkspace?.id, fetchTopics, isPublicRoute])

  useEffect(() => {
    if (data?.mediaSharing && webcamRef?.current?.ref) {
      handleVideoSync(
        webcamRef,
        'webcam',
        sortedMarks,
        position,
        paused,
        isNewSync,
        isNewSync ? newAttemptRecordings : attemptRecordings
      )
    }

    if (data?.screenSharing && screenShareRef?.current?.ref) {
      handleVideoSync(
        screenShareRef,
        'screen',
        sortedMarks,
        position,
        paused,
        isNewSync,
        isNewSync ? newAttemptRecordings : attemptRecordings
      )
    }
  }, [
    paused,
    position,
    isNewSync,
    sortedMarks,
    handleVideoSync,
    attemptRecordings,
    newAttemptRecordings,
    data?.mediaSharing,
    data?.screenSharing
  ])

  return (
    <>
      <SEOComponent title="Evaluate | CoderScout" />
      {(loading?.data ||
        loading?.eventLogs ||
        loading?.topic ||
        loading?.recordings) && <OverlayLoading />}
      <Box className="evaluate-attempt-container">
        <Box className="mcq-header-section">
          <Box className="flex-container">
            <GoBackButton
              publicId={publicId}
              contestId={contestId}
              assessment={assessment}
            />
            {challengeDetails?.name && (
              <Typography>
                <b>{candidateDetails?.name}</b> solution for{' '}
                <b>{challengeDetails?.name}</b>
              </Typography>
            )}
          </Box>
          <Box className="flex-container">
            {!assessment && hasAccess && !isPublicRoute && (
              <RejectQualifyCandidate
                data={attemptStatusDetails}
                refreshHandler={handleRefreshAttemptStatus}
                loading={loading?.status}
              />
            )}
            {selectedFilter !== 'summary' && (
              <RenderScore scoreData={data?.percentageScore} />
            )}
            {!isPublicRoute && <AddRemark attemptId={attemptId} />}
            {isDeveloper && (
              <AddTag
                paused={paused}
                attemptId={attemptId}
                webcamRef={webcamRef}
                screenShareRef={screenShareRef}
                mediaSharing={data?.mediaSharing}
                startTime={sortedMarks?.startTime}
                timelinePosition={position}
                handleRefresh={handleRefreshAttemptEvent}
              />
            )}
            {isNewSync && (
              <EvaluateGazeInfoModal
                attemptId={attemptId}
                startTime={sortedMarks?.startTime}
                onViewGazeTimeline={handleViewGazeTimeline}
              />
            )}

            {isNewSync && isDeveloper && (
              <EvaluateGazeInfoModal
                isViolationModal={true}
                attemptId={attemptId}
                startTime={sortedMarks?.startTime}
                onViewGazeTimeline={handleViewGazeTimeline}
              />
            )}

            <ViewProfile
              contestId={contestId}
              candidateId={candidateDetails?.candidateId}
            />
            {auxiliaryEvidence?.length > 0 ? (
              <AuxiliaryDeviceEvidenceDrawer evidences={auxiliaryEvidence} />
            ) : null}
          </Box>
        </Box>
        <Box className="mcq-main-container">
          <Box className="mcq-side-panel">
            <Box className="marks-breakdown-container">
              <MarksBreakdownItem
                icon="octicon:checklist-16"
                label="Summary"
                isActive={selectedFilter === 'summary'}
                handleBreakdownChange={() => handleSelectFilter('summary')}
              />
              {eventData?.length > 0 && (
                <MarksBreakdownItem
                  icon="mingcute:time-line"
                  label="Timeline"
                  isActive={selectedFilter === 'timeline'}
                  handleBreakdownChange={() => handleSelectFilter('timeline')}
                />
              )}
              <MarksBreakdownItem
                label="All Questions"
                dynamicFont={true}
                isActive={selectedFilter === 'all'}
                value={data?.questions?.length || 0}
                handleBreakdownChange={() => handleSelectFilter('all')}
              />

              {(isNewSync
                ? newAttemptRecordings?.length > 0
                : attemptRecordings?.length > 0) && (
                <PlayRecording
                  className="sidepanel-breakdown-item"
                  recordings={
                    isNewSync ? newAttemptRecordings : attemptRecordings
                  }
                />
              )}
            </Box>
            <Box className="bottom-section">
              <Icon
                title={TOOLTIPS.exit}
                icon={'bitcoin-icons:exit-filled'}
                fontSize={'2rem'}
                onClick={() => handleNavigate()}
                nonHoverBoxStyle={{ cursor: 'pointer' }}
              />

              <Box className="company-logo">
                <img
                  src={mode === 'dark' ? LogoDark : LogoLight}
                  alt="company-logo"
                />
              </Box>
            </Box>
          </Box>
          <Box className="time-previewer-container">
            {selectedFilter === 'timeline' && (
              <Box className="timeline-container">
                <CodeTimeline
                  paused={paused}
                  position={position}
                  marks={sortedMarks}
                  setPaused={setPaused}
                  eventFilter={eventFilter}
                  setPosition={setPosition}
                  handleEventFilterChange={handleEventFilterChange}
                  handleSetTimelineEventData={handleSetTimelineEventData}
                  attemptId={attemptId}
                  handleRefreshAttemptEvent={handleRefreshAttemptEvent}
                  isPublicRoute={isPublicRoute}
                  gazeSegments={gazeSegments}
                />
              </Box>
            )}
            <Box className="previewer-container">
              {selectedFilter !== 'summary' && (
                <McqsPreviewer
                  usedForEvaluation={true}
                  showQuestionMetaData={true}
                  selectedFilter={selectedFilter}
                  allQuestions={filterQuestions}
                  updateQuestionIndex={updateQuestionIndex}
                  setUpdateQuestionIndex={setUpdateQuestionIndex}
                  isPsychometric={
                    data?.stageType === CHALLENGE_TYPES.PSYCHOMETRIC
                  }
                />
              )}
              {selectedFilter === 'summary' && (
                <MCQSummarySection
                  data={data}
                  eventData={eventData}
                  preferredTopics={preferredTopics}
                  navigate={navigate}
                />
              )}
            </Box>
            {data?.screenSharing &&
              (isNewSync
                ? newAttemptRecordings?.length > 0
                : attemptRecordings?.length > 0) &&
              selectedFilter === 'timeline' &&
              !minimiseInfo.screen && (
                <DraggableWindow
                  className="screen-sharing-window"
                  offsetY={10}
                  offsetX={560}
                  zoomed={expandInfo?.screen}
                  zoomedOffsetX={10}
                  zoomedOffsetY={450}
                  modalStyle={
                    showOnTop.screen
                      ? { ...modalStyle, zIndex: '2001 !important' }
                      : modalStyle
                  }
                  draggableProps={{
                    onClick: () =>
                      setShowOnTop((prev) => ({
                        webcam: false,
                        screen: true,
                        auxiliary: false
                      }))
                  }}
                >
                  <Box sx={streamContainerStyle}>
                    <Box className="minimise-container">
                      <Icon
                        hoverEffect
                        title="Resize screen"
                        icon={
                          expandInfo?.screen ? 'mdi:zoom-out' : 'mdi:zoom-in'
                        }
                        hoverBoxStyle={{ zIndex: 2 }}
                        style={{ color: 'var(--common-white)', zIndex: 2 }}
                        onClick={() =>
                          setExpandInfo((prev) => {
                            return {
                              ...prev,
                              screen: !prev?.screen
                            }
                          })
                        }
                      />
                      <Icon
                        className="minimise-icon"
                        hoverEffect
                        title={TOOLTIPS.minimiseScreen}
                        icon={'lsicon:minus-outline'}
                        style={{ color: 'var(--common-white)', zIndex: 2 }}
                        hoverBoxStyle={{ zIndex: 2 }}
                        onClick={() => {
                          screenShareRef.current = {
                            ref: null,
                            duration: 0,
                            currentTime: null,
                            previousUrl: null
                          }
                          setMinimiseInfo((prev) => ({ ...prev, screen: true }))
                        }}
                      />
                    </Box>
                    <video
                      crossOrigin="anonymous"
                      muted
                      ref={(ref) => {
                        screenShareRef.current.ref = ref
                      }}
                      style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '6px',
                        backgroundColor: 'var(--common-black)'
                      }}
                      disablePictureInPicture={true}
                      onLoadedMetadata={() => {
                        screenShareRef.current.duration =
                          screenShareRef?.current?.ref?.duration || 0
                      }}
                      onTimeUpdate={(e) => {
                        screenShareRef.current.currentTime =
                          e?.target?.currentTime || null
                      }}
                    />
                  </Box>
                </DraggableWindow>
              )}
            {data?.mediaSharing &&
              (isNewSync
                ? newAttemptRecordings?.length > 0
                : attemptRecordings?.length > 0) &&
              selectedFilter === 'timeline' &&
              !minimiseInfo.webcam && (
                <DraggableWindow
                  className="webcam-sharing-window"
                  zoomed={expandInfo?.webcam}
                  offsetY={10}
                  offsetX={170}
                  modalStyle={
                    showOnTop.webcam
                      ? { ...modalStyle, zIndex: '2001 !important' }
                      : modalStyle
                  }
                  draggableProps={{
                    onClick: () =>
                      setShowOnTop(() => ({
                        webcam: true,
                        screen: false,
                        auxiliary: false
                      }))
                  }}
                >
                  <Box sx={streamContainerStyle}>
                    <Box className="minimise-container">
                      <Icon
                        hoverEffect
                        title="Resize screen"
                        icon={
                          expandInfo?.webcam ? 'mdi:zoom-out' : 'mdi:zoom-in'
                        }
                        hoverBoxStyle={{ zIndex: 2 }}
                        style={{ color: 'var(--common-white)', zIndex: 2 }}
                        onClick={() =>
                          setExpandInfo((prev) => {
                            return {
                              ...prev,
                              webcam: !prev?.webcam
                            }
                          })
                        }
                      />{' '}
                      <Icon
                        className="minimise-icon"
                        hoverEffect
                        title={TOOLTIPS.minimiseWebcam}
                        icon={'lsicon:minus-outline'}
                        style={{ color: 'var(--common-white)', zIndex: 2 }}
                        hoverBoxStyle={{ zIndex: 2 }}
                        onClick={() => {
                          webcamRef.current = {
                            ref: null,
                            duration: 0,
                            currentTime: null,
                            previousUrl: null
                          }
                          setMinimiseInfo((prev) => ({ ...prev, webcam: true }))
                        }}
                      />
                    </Box>
                    <video
                      crossOrigin="anonymous"
                      ref={(ref) => {
                        webcamRef.current.ref = ref
                      }}
                      style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '6px',
                        backgroundColor: 'var(--common-black)'
                      }}
                      onLoadedMetadata={() => {
                        webcamRef.current.duration =
                          webcamRef?.current?.ref?.duration || 0
                      }}
                      disablePictureInPicture={true}
                      onTimeUpdate={(e) => {
                        webcamRef.current.currentTime =
                          e?.target?.currentTime || null
                      }}
                    />
                  </Box>
                </DraggableWindow>
              )}
            {auxiliaryEvidence?.length > 0 &&
              selectedFilter === 'timeline' &&
              !minimiseInfo.auxiliary && (
                <AuxiliaryDeviceEvidence
                  evidences={auxiliaryEvidence}
                  startTime={sortedMarks?.startTime}
                  showOnTop={showOnTop.auxiliary}
                  position={position}
                  isMinimised={minimiseInfo.auxiliary}
                  onMinimiseChange={(value) =>
                    setMinimiseInfo((prev) => ({ ...prev, auxiliary: value }))
                  }
                  draggableProps={{
                    onClick: () =>
                      setShowOnTop(() => ({
                        webcam: false,
                        screen: false,
                        auxiliary: true
                      }))
                  }}
                />
              )}
          </Box>
          {(minimiseInfo.screen ||
            minimiseInfo.webcam ||
            minimiseInfo.auxiliary) && (
            <Box
              sx={{
                right: 150,
                bottom: 0,
                gap: '16px',
                display: 'flex',
                position: 'absolute',
                padding: '16px 16px 0 16px'
              }}
            >
              {minimiseInfo.screen && (
                <Box
                  className="minimise-item-container"
                  onClick={() => {
                    setMinimiseInfo((prev) => ({ ...prev, screen: false }))
                  }}
                >
                  <Typography>Screen Recording</Typography>
                  <Icon icon={'ic:baseline-launch'} />
                </Box>
              )}
              {minimiseInfo.webcam && (
                <Box
                  className="minimise-item-container"
                  onClick={() => {
                    setMinimiseInfo((prev) => ({ ...prev, webcam: false }))
                  }}
                >
                  <Typography>Webcam Recording</Typography>
                  <Icon icon={'ic:baseline-launch'} />
                </Box>
              )}
              {minimiseInfo.auxiliary && (
                <Box
                  className="minimise-item-container"
                  onClick={() => {
                    setMinimiseInfo((prev) => ({ ...prev, auxiliary: false }))
                  }}
                >
                  <Typography>Auxiliary Device</Typography>
                  <Icon icon={'ic:baseline-launch'} />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </>
  )
}

export default memo(EvaluateMCQAttempt)
